import type { PageEntry, Entry } from 'contensis-delivery-api';
import dynamic from 'next/dynamic';
import htmlParser from 'html-react-parser';
import { v4 as uuidv4 } from 'uuid';
import { format as dateFormatter, parseISO } from 'date-fns';
import { useMemo } from 'react';

import { searchCourseFetchResults } from 'Common/func/courseSearchBox';
import cmsParser from 'Common/util/cmsParser';

import type { CourseSearchProps } from 'Components/Search/Course';

import type { Props } from './Clearing';

export type PageData = {
    entry: PageEntry;
    courses: Entry[];
    ugOpenDay: PageEntry | null;
};

const datamap = (data: PageData): Props => {
    const { entry, courses, ugOpenDay } = data;

    const {
        // Hero
        heroPreheading,
        heroHeading,
        // heroContactsHeading,
        heroCTAHeading,
        phoneNumber,
        phoneLineOpenTimes,
        contactCTAs,
        heroImage,
        heroImageMobile,
        heroPromiseIcon,
        heroPromiseText,
        isClearingOpen,
        geckoEmbedFormUrl,

        courseSearchHeading,
        courseSearchDescription,

        // Flow
        pathHeading,
        firstPathButtonLabel,
        secondPathButtonLabel,
        chooseYourPathInstruction,
        promiseText,
        promiseHeading,

        // Journey one
        gradesVideoTitle,
        gradesVideo,
        gradesDownloadCTAButtons,
        gradesTEFGold,
        gradesWhySolentHeading,
        gradesWhySolentList,
        gradesCourseSearchHeading,
        gradesCourseSearchDescription,
        gradesFoundationYearHeading,
        gradesFoundationYearText,
        gradesFoundationYearList,
        gradesFoundationYearVideo,
        gradesFoundationYearCTA,
        gradesSecondClearingKeyFact,
        gradesStudentVideosHeading,
        gradesStudentVideos,
        gradesOurCityHeading,
        gradesOurCityDescription,
        gradesOurCityVideo,
        gradesThirdClearingKeyFact,
        gradesAccommodationImage,
        gradesAccommodationHeading,
        gradesAccommodationDescription,
        gradesAccommodationCTA,
        gradesFourthClearingKeyFact,

        // Journey Two
        optionsDownloadCTAButtons,
        optionsVideoTitle,
        optionsVideo,
        optionsWhySolentHeading,
        optionsWhySolentList,
        optionsTEFGold,
        optionsCourseSearchHeading,
        optionsCourseSearchDescription,
        optionsStudentVideosHeading,
        optionsStudentVideos,
        optionsOurCityHeading,
        optionsOurCityDescription,
        optionsSecondClearingKeyFact,
        optionsOurCityVideo,
        optionsBitesizeHeading,
        optionsBiteSizeDescription,
        optionsBitesizeVideos,
        optionsThirdClearingKeyFact,
        optionsAccommodationImage,
        optionsAccommodationHeading,
        optionsAccommodationDescription,
        optionsAccommodationCTA,
        optionsFourthClearingKeyFact,

        // Footer
        applyCircleHeading,
        applyHeading,
        applyDescription,
        applyContactCTAs,
        applyOpenDaysHeading,
        applyOpenDaysDescription,
        applyOpenDaysLink,

        // Modal
        enableApplicationModal,
        applicationModal
    } = entry;

    const courseSearch: CourseSearchProps = {
        placeholder: 'Search for a course...',
        search: {
            href: '/courses?search={{search}}',
            ariaLabel: 'Submit your course search'
        },
        label: 'Course search',
        fetchResults: (value: string): { id: string; label: string; href: string }[] =>
            searchCourseFetchResults(courses, value)
    };

    // Add hero icons to memo to stop dyanmic import jolting page when switching journeys
    const heroBannerIcons: React.ElementType = useMemo(() => {
        return contactCTAs.map(contact =>
            dynamic(() =>
                import('@solent-university/solent-icons').then(icons => icons[contact.icon.name])
            )
        );
    }, [contactCTAs]);

    const dynamicIcon = (iconName: string): React.ElementType =>
        useMemo(() => {
            return dynamic(() =>
                import('@solent-university/solent-icons').then(icons => icons[iconName])
            );
        }, []);

    let phoneLinesText = 'Phone lines currently closed.';
    if (phoneLineOpenTimes.length > 0) {
        const currentDate = new Date();
        const currentOpenTime = phoneLineOpenTimes.find(item => {
            if (new Date(item.days.from) <= currentDate && new Date(item.days.to) >= currentDate) {
                return true;
            }
        });

        if (currentOpenTime) {
            phoneLinesText = `Open today: ${currentOpenTime.openingTime} - ${currentOpenTime.closingTime}.`;
        } else {
            const filteredOpenTimes = phoneLineOpenTimes.filter(
                item => new Date().getTime() - new Date(item.days.from).getTime() < 0
            );
            const timesDifferences = filteredOpenTimes.map(
                item => new Date().getTime() - new Date(item.days.from).getTime()
            );

            const nextOpeningTime = timesDifferences.indexOf(Math.max(...timesDifferences));

            if (nextOpeningTime !== -1) {
                phoneLinesText = `Phone lines closed. Open ${
                    filteredOpenTimes[nextOpeningTime].openingTime
                }, ${dateFormatter(
                    parseISO(filteredOpenTimes[nextOpeningTime].days.from),
                    'EEEE d MMMM'
                )}.`;
            }
        }
    }

    return {
        heroBanner: {
            preHeading: heroPreheading,
            heading: heroHeading,
            contactsHeading: heroCTAHeading ? htmlParser(heroCTAHeading) : undefined,
            phone: {
                ...cmsParser.link(phoneNumber),
                id: phoneNumber.link.sys.id,
                icon: dynamicIcon(phoneNumber.icon.name),
                variant: 'large',
                onClick: undefined
            },
            phoneOpeningTimes: phoneLinesText,
            contacts: contactCTAs.map((contact, index) => ({
                ...cmsParser.link(contact),
                id: contact.link.sys.id,
                variant: 'white',
                icon: heroBannerIcons[index]
            })),
            clearingPromise: heroPromiseText ? heroPromiseText : undefined,
            promiseIcon: dynamicIcon(heroPromiseIcon.name),
            images: {
                mobile: cmsParser.image(heroImageMobile),
                desktop: cmsParser.image(heroImage)
            },
            isClearingOpen: isClearingOpen,
            geckoForm: {
                id: 'gecko-form-embed-script',
                url: geckoEmbedFormUrl
            }
        },
        courseSearch: {
            heading: courseSearchHeading,
            text: courseSearchDescription,
            search: courseSearch,
            decoration: 'option1',
            journeyCourseSearch: false
        },
        flow: {
            promise: {
                heading: promiseHeading,
                text: htmlParser(promiseText)
            },
            heading: pathHeading,
            journeyGradesLabel: firstPathButtonLabel,
            journeyOptionsLabel: secondPathButtonLabel,
            desc: htmlParser(chooseYourPathInstruction)
        },
        journeyGrades: {
            intro: {
                videoTitle: gradesVideoTitle,
                video: cmsParser.transcriptEmbed(gradesVideo),
                ctaGroup: {
                    links: gradesDownloadCTAButtons.map((button, index) => ({
                        ...cmsParser.linkButton(button),
                        id: button.link.sys.id,
                        variant: index % 2 ? 'campaignSecondary' : 'campaignPrimary'
                    }))
                }
            },
            whySolent: {
                iconListHeading: gradesWhySolentHeading,
                iconList: {
                    list: gradesWhySolentList.map(item => ({
                        id: uuidv4(),
                        icon: dynamic(() =>
                            import('@solent-university/solent-icons').then(
                                icons => icons[item.icon.name]
                            )
                        ),
                        children: htmlParser(item.listItemText1)
                    }))
                },
                tef: {
                    heading: gradesTEFGold.heading,
                    citation: gradesTEFGold.citation ? gradesTEFGold.citation : undefined
                }
            },
            courseSearch: {
                heading: gradesCourseSearchHeading,
                text: gradesCourseSearchDescription,
                search: courseSearch,
                decoration: 'option2',
                journeyCourseSearch: true
            },
            foundation: {
                heading: gradesFoundationYearHeading,
                text: htmlParser(gradesFoundationYearText),
                iconList: {
                    list: gradesFoundationYearList.map(item => ({
                        id: uuidv4(),
                        icon: dynamic(() =>
                            import('@solent-university/solent-icons').then(
                                icons => icons[item.icon.name]
                            )
                        ),
                        children: htmlParser(item.listItemText1)
                    }))
                },
                stat: {
                    preheading: gradesSecondClearingKeyFact.number,
                    postheading: gradesSecondClearingKeyFact.description,
                    citation: gradesSecondClearingKeyFact.supportingInformation
                },
                video: gradesFoundationYearVideo
                    ? cmsParser.transcriptEmbed(gradesFoundationYearVideo)
                    : undefined,
                button: cmsParser.linkButton(gradesFoundationYearCTA)
            },
            students: {
                heading: gradesStudentVideosHeading,
                duoVideo: [
                    {
                        id: gradesStudentVideos.leftColumnVideo.sys.id,
                        thumbnail: cmsParser.image(gradesStudentVideos.leftColumnThumbnail),
                        video: cmsParser.transcriptEmbed(gradesStudentVideos.leftColumnVideo)
                    },
                    {
                        id: gradesStudentVideos.rightColumnVideo.sys.id,
                        thumbnail: cmsParser.image(gradesStudentVideos.rightColumnThumbnail),
                        video: cmsParser.transcriptEmbed(gradesStudentVideos.rightColumnVideo)
                    }
                ]
            },
            city: {
                supportBlock: {
                    heading: gradesOurCityHeading,
                    text: htmlParser(gradesOurCityDescription),
                    pseudoBlocks: 'option2',
                    decorativeLines: 'option2'
                },
                stat: {
                    preheading: gradesThirdClearingKeyFact.number,
                    postheading: gradesThirdClearingKeyFact.description,
                    citation: gradesThirdClearingKeyFact.supportingInformation
                },
                video: cmsParser.transcriptEmbed(gradesOurCityVideo)
            },
            accommodation: {
                image: cmsParser.image(gradesAccommodationImage),
                supportBlock: {
                    heading: gradesAccommodationHeading,
                    text: htmlParser(gradesAccommodationDescription),
                    button: cmsParser.linkButton(gradesAccommodationCTA),
                    pseudoBlocks: 'option1',
                    decorativeLines: 'option1'
                },
                stat: {
                    preheading: gradesFourthClearingKeyFact.number,
                    postheading: gradesFourthClearingKeyFact.description,
                    citation: gradesFourthClearingKeyFact.supportingInformation
                }
            }
        },
        journeyOptions: {
            intro: {
                ctaGroup: {
                    links: optionsDownloadCTAButtons.map((button, index) => ({
                        ...cmsParser.linkButton(button),
                        id: button.link.sys.id,
                        variant: index % 2 ? 'campaignSecondary' : 'campaignPrimary'
                    })),
                    align: 'left'
                },
                videoTitle: optionsVideoTitle,
                video: cmsParser.transcriptEmbed(optionsVideo)
            },
            whySolent: {
                iconListHeading: optionsWhySolentHeading,
                iconList: {
                    list: optionsWhySolentList.map(item => ({
                        id: uuidv4(),
                        icon: dynamic(() =>
                            import('@solent-university/solent-icons').then(
                                icons => icons[item.icon.name]
                            )
                        ),
                        children: htmlParser(item.listItemText1)
                    }))
                },
                tef: {
                    heading: optionsTEFGold.heading,
                    citation: optionsTEFGold.citation ? optionsTEFGold.citation : undefined
                }
            },
            courseSearch: {
                heading: optionsCourseSearchHeading,
                text: optionsCourseSearchDescription,
                search: courseSearch,
                decoration: 'option2',
                journeyCourseSearch: true
            },
            students: {
                heading: optionsStudentVideosHeading,
                duoVideo: [
                    {
                        id: optionsStudentVideos.leftColumnVideo.sys.id,
                        thumbnail: cmsParser.image(optionsStudentVideos.leftColumnThumbnail),
                        video: cmsParser.transcriptEmbed(optionsStudentVideos.leftColumnVideo)
                    },
                    {
                        id: optionsStudentVideos.rightColumnVideo.sys.id,
                        thumbnail: cmsParser.image(optionsStudentVideos.rightColumnThumbnail),
                        video: cmsParser.transcriptEmbed(optionsStudentVideos.rightColumnVideo)
                    }
                ]
            },
            city: {
                supportBlock: {
                    heading: optionsOurCityHeading,
                    text: htmlParser(optionsOurCityDescription),
                    pseudoBlocks: 'option2',
                    decorativeLines: 'option2'
                },
                stat: {
                    preheading: optionsSecondClearingKeyFact.number,
                    postheading: optionsSecondClearingKeyFact.description,
                    citation: optionsSecondClearingKeyFact.supportingInformation
                },
                video: cmsParser.transcriptEmbed(optionsOurCityVideo)
            },
            bitesize: {
                heading: optionsBitesizeHeading,
                text: optionsBiteSizeDescription,
                videos: optionsBitesizeVideos.map(video => ({
                    id: video.sys.id,
                    thumbnail: cmsParser.image(video.thumbnail),
                    video: cmsParser.transcriptEmbed(video)
                })),
                stat: {
                    preheading: optionsThirdClearingKeyFact.number,
                    postheading: optionsThirdClearingKeyFact.description,
                    citation: optionsThirdClearingKeyFact.supportingInformation
                }
            },
            accommodation: {
                image: cmsParser.image(optionsAccommodationImage),
                supportBlock: {
                    heading: optionsAccommodationHeading,
                    text: htmlParser(optionsAccommodationDescription),
                    button: cmsParser.linkButton(optionsAccommodationCTA),
                    pseudoBlocks: 'option1',
                    decorativeLines: 'option1'
                },
                stat: {
                    preheading: optionsFourthClearingKeyFact.number,
                    postheading: optionsFourthClearingKeyFact.description,
                    citation: optionsFourthClearingKeyFact.supportingInformation
                }
            }
        },
        footer: {
            preHeading: applyCircleHeading,
            heading: applyHeading,
            text: applyDescription,
            phone: {
                ...cmsParser.link(phoneNumber),
                id: `${phoneNumber.link.sys.id}2`,
                icon: dynamicIcon(phoneNumber.icon.name),
                variant: 'large',
                onClick: undefined
            },
            phoneOpeningTimes: phoneLinesText,
            contactLinks: applyContactCTAs.map((contact, index) => ({
                ...cmsParser.link(contact),
                id: contact.link.sys.id,
                variant: index === 0 ? 'default' : 'white',
                icon: dynamic(() =>
                    import('@solent-university/solent-icons').then(
                        icons => icons[contact.icon.name]
                    )
                )
            })),
            signUpLink: {
                id: 'footer-sign-up-link',
                label: 'Sign up now',
                href: '#clearing-hero',
                variant: 'large',
                icon: dynamic(() =>
                    import('@solent-university/solent-icons').then(icons => icons['PaperEdit'])
                )
            },
            postFooter: ugOpenDay
                ? {
                      heading: applyOpenDaysHeading,
                      text: applyOpenDaysDescription,
                      dateLabel: 'Upcoming date:',
                      date: dateFormatter(parseISO(ugOpenDay.date.from), 'EEEE d MMMM'),
                      location: ugOpenDay.location[0] === 'Online event' ? 'Virtual' : 'On campus',
                      link: {
                          ...cmsParser.link(applyOpenDaysLink),
                          variant: 'secondary',
                          iconOutline: true
                      }
                  }
                : undefined,
            isClearingOpen: isClearingOpen
        },
        enableApplicationModal: enableApplicationModal,
        applyModal: {
            heading: applicationModal.heading,
            ukApply: {
                ...cmsParser.link(applicationModal.ukStudents),
                variant: 'default',
                icon: dynamic(() =>
                    import('@solent-university/solent-icons').then(
                        icons => icons[applicationModal.ukStudents.icon.name]
                    )
                ),
                onClick: undefined
            },
            internationalApply: {
                ...cmsParser.link(applicationModal.internationalStudents),
                variant: 'white',
                icon: dynamic(() =>
                    import('@solent-university/solent-icons').then(
                        icons => icons[applicationModal.internationalStudents.icon.name]
                    )
                ),
                // TODO: Why is this needed?
                onClick: undefined
            }
        }
    };
};

export default datamap;
